
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { useSubscription } from '../hooks/useSubscription';
import { useI18n } from '../i18n/i18n';
import { useSEO } from '../hooks/useSEO';
import { useCurrentLanguage } from '../components/LanguageRedirect';
import { getApiBaseUrl } from '../lib/utils';
import { userHasPasswordAuth } from '../lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { 
  User, 
  Mail, 
  CreditCard, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Crown,
  Lock
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from '@/hooks/use-toast';

const ProfilePage = () => {
  const { user } = useAuth();
  const { isSubscribed, subscriptionId, planId, status, loading, cancelAtPeriodEnd, currentPeriodEnd } = useSubscription();
  const { t } = useI18n();
  const navigate = useNavigate();
  const currentLang = useCurrentLanguage();
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

  const handleCancelSubscription = async () => {
    if (!user || !subscriptionId) return;

    setCancellingSubscription(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({
          subscriptionId,
        }),
      });

      if (response.ok) {
        toast({
          title: t('pricing.profile.subscription.cancelSuccess'),
          description: t('pricing.profile.subscription.cancelSuccessDescription'),
        });
        // Optionally refresh the page or update the subscription status
        window.location.reload();
      } else {
        throw new Error('Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast({
        title: t('pricing.profile.subscription.cancelError'),
        description: t('pricing.profile.subscription.cancelErrorDescription'),
        variant: 'destructive',
      });
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = () => {
    // Navegar a la página de precios para que el usuario pueda seleccionar un plan
    navigate(`/${currentLang}/pricing`);
  };

  const getSubscriptionStatusBadge = () => {
    if (loading) return null;
    
    if (!isSubscribed) {
      return (
        <Badge variant="secondary" className="flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          {t('pricing.profile.subscription.inactive')}
        </Badge>
      );
    }

    if (cancelAtPeriodEnd) {
      return (
        <Badge variant="outline" className="border-orange-200 text-orange-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {t('pricing.profile.subscription.cancelled')}
        </Badge>
      );
    }

    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 flex items-center gap-1">
        <CheckCircle className="h-3 w-3" />
        {t('pricing.profile.subscription.active')}
      </Badge>
    );
  };

  const getPlanName = () => {
    if (!planId) return 'Finanzly Free';
    
    switch (planId) {
      case 'price_1RmWHRP1BK1gITTKZrQMwmXY':
        return 'Finanzly Free';
      case 'price_1RmWDqP1BK1gITTKnxqBjzvr':
        return 'Finanzly Plus';
      case 'price_1RmWEaP1BK1gITTKLbZGpYX8':
        return 'Finanzly Pro';
      default:
        return t('pricing.profile.subscription.premiumPlan');
    }
  };

  const formatEndDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000); // Stripe timestamps are in seconds
    return date.toLocaleDateString();
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t('pricing.profile.accessDenied')}</h1>
          <p className="text-neutral-600">{t('pricing.profile.loginRequired')}</p>
        </div>
      </div>
    );
  }

  // SEO Configuration
  const seoContent = useSEO('main', 'profile');

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {seoContent}
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">
          {t('pricing.profile.title')}
        </h1>
        <p className="text-neutral-600">
          {t('pricing.profile.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-[#EA580C]" />
              {t('pricing.profile.accountInfo.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center mb-6">
              <div className="h-20 w-20 rounded-full bg-[#EA580C] flex items-center justify-center text-white text-2xl font-bold">
                {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-neutral-500" />
                <div>
                  <p className="text-sm text-neutral-500">{t('pricing.profile.accountInfo.name')}</p>
                  <p className="font-medium">
                    {user.displayName || t('pricing.profile.accountInfo.noName')}
                  </p>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-neutral-500" />
                <div>
                  <p className="text-sm text-neutral-500">{t('pricing.profile.accountInfo.email')}</p>
                  <p className="font-medium">{user.email}</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-neutral-500" />
                <div>
                  <p className="text-sm text-neutral-500">{t('pricing.profile.accountInfo.memberSince')}</p>
                  <p className="font-medium">
                    {user.metadata.creationTime ? 
                      new Date(user.metadata.creationTime).toLocaleDateString() :
                      t('pricing.profile.accountInfo.unknown')
                    }
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-[#EA580C]" />
              {t('pricing.profile.subscription.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="text-center py-4">
                <p className="text-neutral-500">{t('common.loading')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-500">{t('pricing.profile.subscription.status')}</span>
                  {getSubscriptionStatusBadge()}
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-500">{t('pricing.profile.subscription.plan')}</span>
                  <span className="font-medium">{getPlanName()}</span>
                </div>
                
                {isSubscribed && cancelAtPeriodEnd && currentPeriodEnd && (
                  <>
                    <Separator />
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <span className="text-sm font-medium text-orange-800">
                          {t('pricing.profile.subscription.cancelledNote')}
                        </span>
                      </div>
                      <p className="text-sm text-orange-700 ml-6 mb-3">
                        {formatEndDate(currentPeriodEnd)}
                      </p>
                      <Button 
                        onClick={handleReactivateSubscription}
                        className="w-full bg-gradient-to-r from-orange-400 to-orange-600 text-white hover:from-orange-500 hover:to-orange-700"
                      >
                        <Crown className="h-4 w-4 mr-2" />
                        {t('pricing.profile.subscription.reactivateButton')}
                      </Button>
                    </div>
                  </>
                )}
                
                {isSubscribed && subscriptionId && !cancelAtPeriodEnd && (
                  <>
                    <div className="pt-4">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            {t('pricing.profile.subscription.cancelButton')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2">
                              <AlertTriangle className="h-5 w-5 text-red-500" />
                              {t('pricing.profile.subscription.cancelDialog.title')}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('pricing.profile.subscription.cancelDialog.description')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>
                              {t('common.cancel')}
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleCancelSubscription}
                              disabled={cancellingSubscription}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {cancellingSubscription 
                                ? t('common.loading')
                                : t('pricing.profile.subscription.cancelDialog.confirm')
                              }
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </>
                )}
                
                {!isSubscribed && (
                  <div className="pt-4">
                    <Button className="w-full bg-gradient-to-r from-orange-400 to-orange-600 text-white hover:from-orange-500 hover:to-orange-700">
                      <Crown className="h-4 w-4 mr-2" />
                      {t('pricing.profile.subscription.upgradeButton')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Billing Information */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-[#EA580C]" />
            {t('pricing.profile.billing.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-neutral-600 mb-4">
            {t('pricing.profile.billing.description')}
          </p>
          {isSubscribed ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                {t('pricing.profile.billing.activeSubscription')}
              </p>
            </div>
          ) : (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
              <p className="text-neutral-600 text-sm">
                {t('pricing.profile.billing.noActiveSubscription')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Settings */}
      {userHasPasswordAuth(user) && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-[#EA580C]" />
              {t('pricing.profile.security.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-neutral-900 mb-2">
                  {t('pricing.profile.security.changePassword')}
                </h4>
                <p className="text-sm text-neutral-600 mb-4">
                  {t('pricing.profile.security.changePasswordDescription')}
                </p>
                <Button
                  onClick={() => setChangePasswordModalOpen(true)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Lock className="h-4 w-4" />
                  {t('pricing.profile.security.changePassword')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <ScrollToTopButton />
      
      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={changePasswordModalOpen}
        onClose={() => setChangePasswordModalOpen(false)}
      />
    </div>
  );
};

export default ProfilePage;
